import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Phone, CreditCard, Shield, Lock, ArrowRight, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_API_URL;

export default function OnboardingCheckout() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sessionToken, setSessionToken] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [planInfo, setPlanInfo] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("onboarding_session");
    
    if (!token) {
      navigate("/pricing");
      return;
    }
    
    setSessionToken(token);
    fetchSessionAndPlan(token);
  }, [navigate]);

  const fetchSessionAndPlan = async (token) => {
    try {
      const [sessionRes, plansRes] = await Promise.all([
        axios.get(`${API}/api/v1/onboarding/session/${token}`),
        axios.get(`${API}/api/v1/onboarding/plans`)
      ]);
      
      setSessionData(sessionRes.data);
      
      const plan = plansRes.data.plans.find(p => p.id === sessionRes.data.plan_id);
      setPlanInfo(plan);
    } catch (error) {
      console.error("Failed to fetch session:", error);
      toast.error("Session expired. Please start over.");
      navigate("/pricing");
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const successUrl = `${window.location.origin}/onboarding/success`;
      const cancelUrl = `${window.location.origin}/onboarding/checkout`;
      
      const res = await axios.post(`${API}/api/v1/onboarding/checkout/${sessionToken}`, {
        success_url: successUrl,
        cancel_url: cancelUrl
      });
      
      if (res.data.success && res.data.checkout_url) {
        // Redirect to Stripe Checkout
        window.location.href = res.data.checkout_url;
      } else {
        throw new Error(res.data.error || "Checkout failed");
      }
    } catch (error) {
      const message = error.response?.data?.detail || error.message || "Checkout failed";
      toast.error(message);
      setLoading(false);
    }
  };

  if (!planInfo) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 bg-slate-900/50">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Phone className="w-8 h-8 text-blue-500" />
            <span className="text-xl font-bold">Speakr</span>
          </div>
          <div className="flex items-center gap-4 text-sm text-slate-400">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
              <span className="hidden md:inline">Plan</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
              <span className="hidden md:inline">Details</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center text-white text-sm">✓</div>
              <span className="hidden md:inline">Verify</span>
            </div>
            <div className="w-8 h-px bg-slate-700"></div>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-bold">4</div>
              <span className="hidden md:inline">Payment</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 max-w-2xl">
        <div className="grid md:grid-cols-5 gap-8">
          {/* Order Summary */}
          <div className="md:col-span-2">
            <Card className="bg-slate-900 border-slate-800 sticky top-6">
              <CardHeader>
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="font-medium">{planInfo.name}</p>
                    <p className="text-sm text-slate-400">Monthly subscription</p>
                  </div>
                  <p className="font-bold">${planInfo.price_monthly}/mo</p>
                </div>
                
                <div className="border-t border-slate-700 pt-4 space-y-2 text-sm">
                  <div className="flex justify-between text-slate-400">
                    <span>Minutes included</span>
                    <span>{planInfo.minutes_included === -1 ? "Unlimited" : planInfo.minutes_included.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>AI Agents</span>
                    <span>{planInfo.agents === -1 ? "Unlimited" : planInfo.agents}</span>
                  </div>
                  <div className="flex justify-between text-slate-400">
                    <span>Trial period</span>
                    <span>{planInfo.trial_days} days</span>
                  </div>
                </div>

                <div className="border-t border-slate-700 pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-slate-400">Due today</span>
                    <span className="text-2xl font-bold text-green-400">$0.00</span>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    First charge of ${planInfo.price_monthly} on{" "}
                    {new Date(Date.now() + planInfo.trial_days * 24 * 60 * 60 * 1000).toLocaleDateString()}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Section */}
          <div className="md:col-span-3">
            <Card className="bg-slate-900 border-slate-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-3">
                  <CreditCard className="w-6 h-6 text-blue-500" />
                  Secure Payment
                </CardTitle>
                <CardDescription className="text-slate-400">
                  You'll be redirected to Stripe's secure checkout
                </CardDescription>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Trust Badges */}
                <div className="grid grid-cols-3 gap-4 p-4 bg-slate-800/50 rounded-lg">
                  <div className="text-center">
                    <Shield className="w-6 h-6 text-green-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">SSL Encrypted</p>
                  </div>
                  <div className="text-center">
                    <Lock className="w-6 h-6 text-green-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">3D Secure</p>
                  </div>
                  <div className="text-center">
                    <CreditCard className="w-6 h-6 text-green-400 mx-auto mb-1" />
                    <p className="text-xs text-slate-400">PCI Compliant</p>
                  </div>
                </div>

                {/* What happens next */}
                <div className="space-y-3">
                  <p className="font-medium">What happens next:</p>
                  <ol className="space-y-2 text-sm text-slate-400">
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">1</span>
                      <span>Enter your card details on Stripe's secure page</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">2</span>
                      <span>Your {planInfo.trial_days}-day free trial starts immediately</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">3</span>
                      <span>Set up your AI agent in our guided onboarding wizard</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 flex items-center justify-center text-xs flex-shrink-0 mt-0.5">4</span>
                      <span>Start receiving AI-powered calls right away!</span>
                    </li>
                  </ol>
                </div>

                {/* CTA Button */}
                <Button 
                  className="w-full bg-blue-500 hover:bg-blue-600 h-12 text-lg"
                  onClick={handleCheckout}
                  disabled={loading}
                  data-testid="checkout-button"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"></div>
                      Redirecting to Stripe...
                    </>
                  ) : (
                    <>
                      Start Free Trial
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>

                {/* Fine print */}
                <p className="text-xs text-slate-500 text-center">
                  By proceeding, you agree to our{" "}
                  <a href="/terms" className="text-blue-400 hover:underline">Terms of Service</a>
                  {" "}and{" "}
                  <a href="/privacy" className="text-blue-400 hover:underline">Privacy Policy</a>.
                  <br />
                  Cancel anytime during your trial with no charge.
                </p>
              </CardContent>
            </Card>

            {/* Contact */}
            <p className="text-center text-sm text-slate-500 mt-6">
              Questions? Call{" "}
              <a href="tel:+12893356542" className="text-blue-400 hover:underline">
                +1 (289) 335-6542
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
